import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnInit,
  output,
  Type,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { modalFn, UiCoreDirectivesModule, UiCoreModule } from '@softline/ui-core';
import {
  queryParam,
  routeFragment,
  scan,
  Scan,
  ScanButtonComponent,
} from '@softline/application';
import { SearchStrategy } from './search.strategy';
import { BehaviorSubject, debounceTime, distinctUntilChanged } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  SearchResultComponent,
  SOFTLINE_CONFIG_SEARCH_PAGE_RESULT_VIEW,
} from './abstraction/search-result.component';
import { ActivatedRoute, Router } from '@angular/router';
import { PageComponent } from '../page/page.component';
import { isDefined } from '@softline/core';
import { PageTabComponent } from '../page/page-tab/page-tab.component';

@Component({
  selector: 'soft-search-page',
  imports: [
    CommonModule,
    UiCoreDirectivesModule,
    ScanButtonComponent,
    UiCoreModule,
    PageComponent,
    PageTabComponent,
  ],
  templateUrl: './search-page.component.html',
  styleUrl: './search-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchPageComponent<T> implements OnInit {
  private readonly router = inject(Router);
  private route = inject(ActivatedRoute);
  private readonly modal = modalFn();
  private routeFragment = routeFragment();
  private searchTerm = queryParam('searchTerm');

  readonly initialSearchTerm = input<string>('');

  readonly searchInput$ = new BehaviorSubject<string | null>(null);

  readonly debouncedSearchInput = toSignal(
    this.searchInput$.pipe(debounceTime(300), distinctUntilChanged()),
    { initialValue: null }
  );

  canScan = input(true);
  autoSearch = input(true);
  observeContentSize = input(true);

  readonly select = output<T>();
  readonly favoriteChange = output<T>();
  readonly scan = output<Scan>();

  readonly strategy = inject(SearchStrategy);
  readonly overrideResultComponent = inject(SOFTLINE_CONFIG_SEARCH_PAGE_RESULT_VIEW, {
    optional: true,
  });

  get resultComponent(): Type<SearchResultComponent<T>> {
    return this.overrideResultComponent ?? this.strategy.resultViewType;
  }

  private readonly _onSearchInputChangeEffect = effect(async () => {
    const searchInput = this.debouncedSearchInput();
    this.strategy.searchInputChange(searchInput ?? null);
    await this.router.navigate(['./'], {
      relativeTo: this.route,
      queryParams: !searchInput
        ? null
        : {
            searchTerm: searchInput,
          },
      preserveFragment: true,
    });
  });

  constructor() {
    scan(async (o) => this.scan.emit(o));
    effect(() => {
      if (this.strategy.isManualSearchActive()) {
        this.searchInput$.next(null);
      }
    });
  }

  async search() {
    await this.strategy.search(this.searchInput$.value);
  }

  onFavoriteChange(value: T): void {
    this.strategy.favoriteChange(value);
    this.favoriteChange.emit(value);
  }

  async ngOnInit(): Promise<void> {
    const queryParamsSearchTerm = this.searchTerm();

    if (queryParamsSearchTerm) {
      this.searchInput$.next(queryParamsSearchTerm);
    }

    if (this.initialSearchTerm) {
      this.searchInput$.next(this.initialSearchTerm());
    }

    const fragment = this.routeFragment();

    if (!isDefined(fragment))
      await this.router.navigate([], {
        fragment: 'search',
        // do not push into browser history stack so the browser back button works as expected
        skipLocationChange: true,
        relativeTo: this.route,
      });
  }

  async openQueryModalView(): Promise<void> {
    const component = this.strategy.queryModalViewType;

    if (!component) return;

    const result = await this.modal({
      component,
      title: 'Abfrage',
      dismiss: true,
    });

    if (!result || result === 'DISMISSED' || result === 'Dismissed') return;

    await this.strategy.modalResolved(result);
  }

  onSelect(value: T): void {
    this.strategy.addToLastUsed(value);
    this.select.emit(value);
  }

  removeFilter(filter: { query: object; fullValue?: object }, key: string): void {
    if (filter.query && Object.keys(filter.query).includes(key)) {
      delete filter.query[key];
    }

    if (filter?.fullValue && Object.keys(filter.fullValue).includes(key)) {
      delete filter.fullValue[key];
    }

    this.strategy.setFilter(filter);
  }
}
